// Global auth + membership provider.
//
// Flow:
//   1. Subscribe to Firebase Auth state.
//   2. Resolve the user's company memberships using the same path sequence as
//      the iOS app: read `users/{uid}.companyIds[]`, then fetch each
//      `companies/{companyId}/members/{uid}` document directly.
//   3. Expose the list of memberships + the currently-selected one for role gating.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  type User,
} from 'firebase/auth';
import {
  doc,
  getDoc,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { resolveActiveMemberships } from './membershipLoading';
import { withReadTimeout } from '../lib/readLoading';
import { listenDocument } from '../lib/liveRead';
import {
  canManageCompany,
  ALL_MEMBER_ROLES,
  type CompanyMember,
  type MemberRole,
  type UserDoc,
} from '../types/models';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  memberships: CompanyMember[];
  activeCompanyId: string | null;
  activeRole: MemberRole | null;
  isAdmin: boolean;
  /** Map of companyId → human-readable company name (falls back to id). */
  companyNames: Record<string, string>;
  /** Friendly name for the active company. */
  activeCompanyName: string | null;
  /** Resolve a friendly name for any company the user belongs to. */
  companyName: (companyId: string | null | undefined) => string;
  setActiveCompanyId: (companyId: string) => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  refreshMemberships: () => Promise<void>;
  /** Last auth error surfaced to UI, if any. */
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ACTIVE_COMPANY_KEY = 'picup-admin.activeCompanyId';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<CompanyMember[]>([]);
  const [activeCompanyId, setActiveCompanyIdState] = useState<string | null>(
    () => {
      try {
        return localStorage.getItem(ACTIVE_COMPANY_KEY);
      } catch {
        return null;
      }
    }
  );
  const [error, setError] = useState<string | null>(null);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const membershipRequest = useRef(0);
  const [watchedCompanyId, setWatchedCompanyId] = useState<string | null>(null);
  const watchTarget = useRef<string | null>(null);

  const loadMemberships = useCallback(
    async (u: User): Promise<CompanyMember[]> => {
      // Same as iOS AuthManager.getUserCompanies():
      // users/{uid}.companyIds[] → companies/{companyId}/members/{uid}
      try {
        const userSnap = await withReadTimeout(getDoc(doc(db, 'users', u.uid)));
        if (!userSnap.exists()) return [];

        const ud = userSnap.data() as Partial<UserDoc>;
        return await resolveActiveMemberships(
          Array.isArray(ud.companyIds) ? ud.companyIds : [],
          async (companyId) => {
            const snap = await withReadTimeout(getDoc(
              doc(db, 'companies', companyId, 'members', u.uid)
            ));
            if (!snap.exists()) return null;
            return memberFromSnapshot(snap);
          }
        );
      } catch (e) {
        console.warn('[auth] users/{uid} companyIds lookup failed', e);
        throw e;
      }
    },
    []
  );

  const loadForUser = useCallback(
    async (u: User | null) => {
      const request = ++membershipRequest.current;
      const current = () => request === membershipRequest.current && auth.currentUser?.uid === u?.uid;
      setLoading(true);
      setError(null);
      setMemberships([]);
      setCompanyNames({});
      setActiveCompanyIdState(null);
      setWatchedCompanyId(null);
      watchTarget.current = null;
      if (!u) {
        setLoading(false);
        return;
      }
      try {
        const mems = await loadMemberships(u);
        if (!current()) return;
        setMemberships(mems);

        // Pick active company: stored → first admin-capable → first
        const stored = (() => {
          try {
            return localStorage.getItem(ACTIVE_COMPANY_KEY);
          } catch {
            return null;
          }
        })();

        const storedValid = stored && mems.some((m) => m.companyId === stored);
        if (storedValid) {
          setActiveCompanyIdState(stored);
          setWatchedCompanyId(stored);
          watchTarget.current = stored;
        } else {
          const firstAdmin = mems.find((m) => canManageCompany(m.role));
          const first = firstAdmin ?? mems[0];
          if (first) {
            setActiveCompanyIdState(first.companyId);
            setWatchedCompanyId(first.companyId);
            watchTarget.current = first.companyId;
            try {
              localStorage.setItem(ACTIVE_COMPANY_KEY, first.companyId);
            } catch {
              /* noop */
            }
          } else {
            setActiveCompanyIdState(null);
          }
        }
      } catch (e) {
        if (!current()) return;
        console.error('[auth] failed to load memberships', e);
        const msg = e instanceof Error ? e.message : String(e);
        setError(`Failed to load your company memberships: ${msg}`);
      } finally {
        if (current()) setLoading(false);
      }
    },
    [loadMemberships]
  );

  const refreshMemberships = useCallback(() => loadForUser(auth.currentUser), [loadForUser]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      void loadForUser(u);
    });
    return () => {
      membershipRequest.current += 1;
      unsub();
    };
  }, [loadForUser]);

  // Keep watching a disabled membership so a server-confirmed reactivation can
  // reopen it. Switching company or account cancels the old watch.
  useEffect(() => {
    const uid = user?.uid;
    if (!uid || !watchedCompanyId) return;
    let current = true;
    const stillCurrent = () => current && auth.currentUser?.uid === uid && watchTarget.current === watchedCompanyId;
    const closeCompany = (message: string) => {
      if (!stillCurrent()) return;
      setMemberships((members) => members.filter((m) => m.companyId !== watchedCompanyId));
      setActiveCompanyIdState((id) => id === watchedCompanyId ? null : id);
      setError(message);
    };
    const unsubscribe = listenDocument(
      doc(db, 'companies', watchedCompanyId, 'members', uid),
      (snapshot) => {
        if (!stillCurrent()) return;
        const member = snapshot.exists() ? memberFromSnapshot(snapshot) : null;
        if (!member || member.isActive !== true || !ALL_MEMBER_ROLES.includes(member.role)) {
          closeCompany('Your membership in this company is no longer active.');
          return;
        }
        setMemberships((members) => {
          const old = members.find((m) => m.companyId === watchedCompanyId);
          if (old && JSON.stringify(old) === JSON.stringify(member)) return members;
          return [...members.filter((m) => m.companyId !== watchedCompanyId), member];
        });
        setError(null);
        setActiveCompanyIdState((id) => id ?? watchedCompanyId);
      },
      (failure) => closeCompany(`Unable to verify current company access: ${failure.message}`)
    );
    return () => { current = false; unsubscribe(); };
  }, [user?.uid, watchedCompanyId]);

  // Resolve friendly company names for the sidebar / company picker.
  useEffect(() => {
    if (memberships.length === 0) {
      setCompanyNames({});
      return;
    }
    let cancelled = false;
    void Promise.all(
      memberships.map(async (m) => {
        try {
          const snap = await withReadTimeout(getDoc(doc(db, 'companies', m.companyId)));
          const name = snap.exists()
            ? ((snap.data() as { name?: string }).name ?? '').trim()
            : '';
          return [m.companyId, name] as const;
        } catch {
          return [m.companyId, ''] as const;
        }
      })
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [id, name] of entries) if (name) next[id] = name;
      setCompanyNames(next);
    });
    return () => {
      cancelled = true;
    };
  }, [memberships]);

  const setActiveCompanyId = useCallback((companyId: string) => {
    if (!memberships.some((member) => member.companyId === companyId && member.isActive === true)) return;
    setActiveCompanyIdState(companyId);
    setWatchedCompanyId(companyId);
    watchTarget.current = companyId;
    setError(null);
    try {
      localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
    } catch {
      /* noop */
    }
  }, [memberships]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Sign-in failed';
      setError(message);
      throw e;
    }
  }, []);

  const signOutUser = useCallback(async () => {
    await signOut(auth);
    try {
      localStorage.removeItem(ACTIVE_COMPANY_KEY);
    } catch {
      /* noop */
    }
  }, []);

  const activeRole: MemberRole | null = useMemo(() => {
    if (!activeCompanyId) return null;
    return (
      memberships.find((m) => m.companyId === activeCompanyId)?.role ?? null
    );
  }, [memberships, activeCompanyId]);

  const companyName = useCallback(
    (companyId: string | null | undefined): string => {
      if (!companyId) return '';
      return companyNames[companyId] || companyId;
    },
    [companyNames]
  );

  const activeCompanyName = useMemo(
    () => (activeCompanyId ? companyName(activeCompanyId) : null),
    [activeCompanyId, companyName]
  );

  const value: AuthContextValue = useMemo(
    () => ({
      user,
      loading,
      memberships,
      activeCompanyId,
      activeRole,
      isAdmin: canManageCompany(activeRole ?? undefined),
      companyNames,
      activeCompanyName,
      companyName,
      setActiveCompanyId,
      signIn,
      signOutUser,
      refreshMemberships,
      error,
    }),
    [
      user,
      loading,
      memberships,
      activeCompanyId,
      activeRole,
      companyNames,
      activeCompanyName,
      companyName,
      setActiveCompanyId,
      signIn,
      signOutUser,
      refreshMemberships,
      error,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function memberFromSnapshot(
  snap: DocumentSnapshot
): CompanyMember {
  const data = snap.data() as Omit<CompanyMember, 'id'>;
  const companyId = snap.ref.parent.parent?.id ?? data.companyId;
  return { ...data, id: snap.id, userId: snap.id, companyId } as CompanyMember;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
